<template>
    <div class="date-picker-mobile-container">
        <div class="date-picker-mobile-wrapper">
            <div class="info">
                <p class="title">Let's Pick Some Dates</p>
                <div class="settings">
                    <div
                        @click="handleSelectRange"
                        :class="mode === 'range' ? 'selected' : ''"
                        class="range-select-input"
                    >
                        <span class="custom-radio"></span>
                        <label for="date-select">Select Date Range</label>
                    </div>
                    <div
                        @click="handleSelectIndividual"
                        :class="mode === 'individual' ? 'selected' : ''"
                        class="individual-select-input"
                    >
                        <span class="custom-radio"></span>
                        <label for="date-select">Select Individual Dates</label>
                    </div>
                </div>
            </div>
            <date-filter
                :additional-months="6"
                :calendar.sync="calendar"
                :styleOptions="calendarStyle"
                @dateClicked="handleDatepick"
            >
                <template #month-label="{ month }">
                    <div class="month-label-wrapper">
                        {{ month.label }}
                    </div>
                </template>
                <template #paginate-actions="{ handleNextMonths, handlePrevMonths }">
                    <div class="paginate-actions">
                        <div @click="handlePrevMonths" class="prev-months"></div>
                        <div @click="handleNextMonths" class="next-months"></div>
                    </div>
                </template>
            </date-filter>
            <div
                v-if="firstSelectedDate || selectedIndividualDates.length"
                class="selected-dates clear-dates"
            >
                <div>
                    Selected: <span class="target-dates">{{ selected }}</span>
                </div>
                <p @click="clearSelectedDates" class="clear-selected-dates">
                    Clear dates selection
                </p>
            </div>
            <div class="action-btns">
                <button @click="handleSelectBtn" class="select-btn">Select Range</button>
                <button @click="handleCancelBtn" class="cancel-btn">Cancel</button>
            </div>
        </div>
    </div>
</template>
<script>
    import SingleBookingView from '@/components/ui/calendars/SingleBookingView.vue'
    import { MONTHS } from '@/constants/calendar'

    export default {
        name: 'DatepickerMobile',

        props: {
            pickedDates: {
                required: false,
                type: Array,
                default: () => ([])
            },
            mode: {
                required: false,
                type: String,
                default: null
            }
        },

        emits: ['closeDatepicker', 'saveDates', 'update:mode'],

        components: {
            'date-filter': SingleBookingView
        },

        data: () => ({
            calendar: [],
            currentDateRange: [],
            selectedIndividualDates: [],
            firstSelectedDate: null,
            secondSelectedDate: null,
            calendarStyle: {
                container: {
                    position: 'relative'
                }
            }
        }),

        methods: {
            clearSelectedDates() {
                if (this.mode === 'range') {
                    this.clearRangeDates(this.currentDateRange)
                    this.firstSelectedDate = null
                    this.secondSelectedDate = null
                    this.currentDateRange.splice(0)
                } else {
                    this.clearIndividualDates(this.selectedIndividualDates)
                    this.selectedIndividualDates.splice(0)
                }
            },
            handleCancelBtn() {
                this.$emit('closeDatepicker')
            },

            handleSelectBtn() {
                if (
                    this.mode === 'range' &&
                    this.firstSelectedDate &&
                    this.secondSelectedDate
                ) {
                    this.$emit('saveDates', this.convertDateRangeToDates())
                } else if (
                    this.mode === 'range' &&
                    this.firstSelectedDate &&
                    !this.secondSelectedDate
                ) {
                    this.$emit('saveDates', [this.firstSelectedDate])
                } else if (
                    this.mode === 'range' &&
                    !this.firstSelectedDate &&
                    !this.secondSelectedDate
                ) {
                    this.$emit('saveDates', [])
                } else if (this.mode === 'individual') {
                    this.$emit('saveDates', this.selectedIndividualDates)
                }
            },

            handleSelectRange() {
                this.mode = 'range'
            },

            handleSelectIndividual() {
                this.mode = 'individual'
            },

            handleDateRange(firstDate, secondDate) {
                if (firstDate && secondDate) {
                    let datesBetween = this.getDatesBetween(firstDate, secondDate)

                    if (this.currentDateRange.length) this.unhighlightDateRange()
                    this.highlightDateRange(datesBetween)
                }
            },

            handleDatepick(label, day) {
                let date = this.convertLabelToDate(label, day)
                this.selectDate(date, day)
            },

            getDatesBetween(firstDate, secondDate) {
                let datesBetween = []
                let currentDate = new Date(firstDate.valueOf())

                if (currentDate < secondDate) {
                    while (currentDate < secondDate) {
                        datesBetween.push({
                            y: currentDate.getFullYear(),
                            m: currentDate.getMonth(),
                            d: currentDate.getDate()
                        })

                        currentDate.setDate(currentDate.getDate() + 1)
                    }
                } else {
                    while (currentDate > secondDate) {
                        datesBetween.push({
                            y: currentDate.getFullYear(),
                            m: currentDate.getMonth(),
                            d: currentDate.getDate()
                        })

                        currentDate.setDate(currentDate.getDate() - 1)
                    }
                }

                return datesBetween
            },

            selectDate(date, day) {
                if (this.mode === 'range') {
                    if (this.firstSelectedDate === null && this.secondSelectedDate === null) {
                        this.firstSelectedDate = date

                        this.$set(day, 'selected', true)
                    } else {
                        this.unselectSecondDate()
                        this.secondSelectedDate = date

                        this.$set(day, 'selected', true)
                    }
                } else {
                    if (!day.selected) {
                        this.selectedIndividualDates.push(date)
                        this.$set(day, 'selected', true)
                    } else {
                        this.selectedIndividualDates = this.selectedIndividualDates.filter(
                            (d) => d.valueOf() !== date.valueOf()
                        )
                        this.$set(day, 'selected', false)
                    }
                }
            },

            unselectSecondDate() {
                if (!this.secondSelectedDate) return

                let label = this.convertDateToLabel(this.secondSelectedDate)
                let date = this.secondSelectedDate.getDate()

                for (let i = 0; i < this.calendar.length; i++) {
                    if (this.calendar[i].label === label) {
                        let secondDate = this.calendar[i].days.find((day) => day.day === date)

                        this.$set(secondDate, 'selected', false)
                    }
                }
            },

            getTargetDate(label, day) {
                return this.calendar
                    .find((month) => month.label === label)
                    .days.find((d) => d.day === day)
            },

            highlightDateRange(dateRange) {
                this.currentDateRange = dateRange

                for (let i = 0; i < dateRange.length; i++) {
                    let day = dateRange[i].d
                    let label = this.convertDateToLabel(
                        new Date(dateRange[i].y, dateRange[i].m)
                    )
                    let targetDate = this.getTargetDate(label, day)

                    this.$set(targetDate, 'highlighted', true)
                }
            },

            unhighlightDateRange() {
                for (let i = 0; i < this.currentDateRange.length; i++) {
                    let day = this.currentDateRange[i].d
                    let label = this.convertDateToLabel(
                        new Date(this.currentDateRange[i].y, this.currentDateRange[i].m)
                    )
                    let targetDate = this.getTargetDate(label, day)

                    this.$set(targetDate, 'highlighted', false)
                }
            },

            appendDateSuffix(i) {
                i = parseInt(i)

                let j = i % 10,
                    k = i % 100

                if (j == 1 && k != 11) {
                    return i + 'st'
                }
                if (j == 2 && k != 12) {
                    return i + 'nd'
                }
                if (j == 3 && k != 13) {
                    return i + 'rd'
                }
                return i + 'th'
            },

            convertDateToLabel(date) {
                let year = date.getFullYear()
                let month = date.getMonth()

                return `${MONTHS[month]} ${year}`
            },

            convertDateToDayObj(date) {
                let label = this.convertDateToLabel(date)
                let day = date.getDate()

                return this.calendar
                    .find((month) => month.label === label)
                    .days.find((d) => d.day === day)
            },

            convertLabelToDate(label, day) {
                let year = label.split(' ')[1]
                let month = Object.keys(MONTHS).find(
                    (key) => MONTHS[key] === label.split(' ')[0]
                )

                return new Date(year, month, day.day)
            },

            convertDateRangeToDates() {
                let dates = []
                let dateRange = this.currentDateRange

                for (let i = 1; i < dateRange.length; i++) {
                    dates.push(new Date(dateRange[i].y, dateRange[i].m, dateRange[i].d))
                }

                dates.unshift(this.firstSelectedDate)
                dates.push(this.secondSelectedDate)

                return dates
            },

            clearRangeDates(dateRange) {
                this.clearIndividualDates([this.firstSelectedDate, this.secondSelectedDate])
                this.unhighlightDateRange(dateRange)

                this.firstSelectedDate = null
                this.secondSelectedDate = null
                this.currentDateRange = []
            },

            clearIndividualDates(dates) {
                for (let i = 0; i < dates.length; i++) {
                    if (dates[i]) {
                        let day = this.convertDateToDayObj(dates[i])

                        day.highlighted = false
                        day.selected = false
                    }
                }

                this.selectedIndividualDates = []
            },

            resolveRangeSelected() {
                let selected = ''

                if (this.firstSelectedDate) {
                    let label = this.convertDateToLabel(this.firstSelectedDate).split(' ')
                    let day = this.appendDateSuffix(this.firstSelectedDate.getDate())
                    let month = label[0]
                    let year = label[1]

                    selected = `${month} ${day}, ${year}`
                }

                if (this.secondSelectedDate) {
                    let label = this.convertDateToLabel(this.secondSelectedDate).split(' ')
                    let day = this.appendDateSuffix(this.secondSelectedDate.getDate())
                    let month = label[0]
                    let year = label[1]

                    if (
                        this.firstSelectedDate &&
                        this.firstSelectedDate > this.secondSelectedDate
                    ) {
                        selected = `${month} ${day}, ${year}-` + selected
                    }

                    selected = selected + `-${month} ${day}, ${year}`
                }

                return selected
            },

            resolveIndividualSelected() {
                let selected = ''

                for (let i = 0; i < this.selectedIndividualDates.length; i++) {
                    let label = this.convertDateToLabel(this.selectedIndividualDates[i]).split(
                        ' '
                    )
                    let day = this.appendDateSuffix(this.selectedIndividualDates[i].getDate())
                    let month = label[0]
                    let year = label[1]
                    selected = selected + ` ${month} ${day}, ${year}`
                }

                return selected
            },

            formatSelectedDates(selectedDates) {
                if (!selectedDates.includes('-')) return selectedDates

                selectedDates = selectedDates.split('-')

                let firstDate = selectedDates[0]
                let secondDate = selectedDates[1]
                let firstYear = firstDate.split(' ')[2]
                let secondYear = secondDate.split(' ')[2]

                if (firstYear === secondYear) {
                    return `${firstDate.replace(`, ${firstYear}`, '')}-${secondDate}`
                } else {
                    return `${selectedDates[0]}-${selectedDates[1]}`
                }
            },

            populatePickedDates() {
                this.mode === 'individual'
                    ? this.populateIndividualDates()
                    : this.populateRangeDates()
            },

            populateRangeDates() {
                const firstDay = this.convertDateToDayObj(this.pickedDates[0])
                const lastDay = this.convertDateToDayObj(this.pickedDates[this.pickedDates.length - 1])

                this.$nextTick(() => {
                    this.selectDate(this.pickedDates[0], firstDay)
                })
                this.$nextTick(() => {
                    this.selectDate(this.pickedDates[this.pickedDates.length - 1], lastDay)
                })
            },

            populateIndividualDates() {
                for (let i = 0; i < this.pickedDates.length; i++) {
                    const date = this.pickedDates[i]
                    const day = this.convertDateToDayObj(date)

                    this.$nextTick(() => {
                        this.selectDate(date, day)
                    })
                }
            }
        },

        computed: {
            selected: (self) => {
                let selected = ''

                if (self.mode === 'range') {
                    selected = self.resolveRangeSelected()
                } else {
                    selected = self.resolveIndividualSelected()
                }
                return self.formatSelectedDates(selected)
            }
        },

        watch: {
            mode: function (newMode, oldMode) {
                this.$emit('update:mode', newMode)

                if (newMode === 'individual' && oldMode === 'range') {
                    this.clearRangeDates(this.currentDateRange)
                } else if (newMode === 'range' && oldMode === 'individual') {
                    this.clearIndividualDates(this.selectedIndividualDates)
                }
            },

            secondSelectedDate: function (secondDate) {
                if (this.mode === 'range')
                    this.handleDateRange(this.firstSelectedDate, secondDate)
            },

            calendar: function (calendar) {
                if (calendar.length && this.pickedDates.length) this.populatePickedDates()
            }
        }
    }
</script>
<style lang="scss" scoped>
    .clear-dates {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }
    .date-picker-mobile-container {

        max-width: 390px;
        p {
            margin-bottom: 0px;
        }

        .date-picker-mobile-wrapper {
            .month-label-wrapper {
                display: flex;
                flex-direction: row;
                justify-content: center;
            }

            .info {
                margin-top: 91px;
                margin-bottom: 60px;
                display: flex;
                flex-direction: column;
                gap: 12px;

                .title {
                    color: #dedede;
                    font-family: Instrument Sans;
                    font-size: 44px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 110%;
                    letter-spacing: -1.76px;
                }

                .settings {
                    display: flex;
                    flex-direction: row;
                    gap: 8px;

                    .range-select-input,
                    .individual-select-input {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        label {
                            cursor: grab;
                            color: #8b8b8b;
                            font-family: Instrument Sans;
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 550;
                            line-height: 125%;
                        }

                        .custom-radio {
                            width: 18px;
                            height: 18px;
                            border-radius: 20px;
                            border: 1.5px solid #8b8b8b;
                        }
                    }

                    .range-select-input,
                    .individual-select-input:hover {
                        cursor: grab;
                    }

                    .range-select-input.selected,
                    .individual-select-input.selected {
                        label {
                            color: #cdeb09;
                            font-family: Instrument Sans;
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 550;
                            line-height: 125%;
                        }

                        .custom-radio {
                            width: 18px;
                            height: 18px;
                            border-radius: 20px;
                            border: 1.5px solid #cdeb09;
                            background: #cdeb09;
                        }
                    }
                }
            }

            .paginate-actions {
                display: flex;
                flex-direction: row;
                justify-content: space-between;

                .next-months {
                    width: 16.743px;
                    height: 16.743px;
                    background: url(/images/icons/next.svg) no-repeat;
                    background-size: 16.743px 16.743px;
                    position: absolute;
                    top: 2%;
                    right: 7%;
                }

                .prev-months {
                    width: 16.743px;
                    height: 16.743px;
                    background: url(/images/icons/prev.svg) no-repeat;
                    background-size: 16.743px 16.743px;
                    position: absolute;
                    top: 2%;
                    left: 7%;
                }
            }

            .selected-dates {
                color: #acacac;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
                padding-left: 24px;
                padding-right: 24px;

                .target-dates {
                    color: #cdeb09;
                    font-family: Instrument Sans;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 125%;
                }

                .clear-selected-dates {
                    cursor: pointer;
                    text-decoration: underline;
                }
            }

            .action-btns {
                margin-top: 50px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;

                .select-btn {
                    max-width: 600px;
                    width: 100%;
                    padding: 11px 30px 12px 30px;
                    border-radius: 100px;
                    background: #cdeb09;
                    color: black;
                    font-family: Instrument Sans;
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 120%;
                    border: none;
                }
                .cancel-btn {
                    margin-top: 25px;
                    margin-bottom: 30px;
                    color: #8b8b8b;
                    font-family: Studio Pro;
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 120%;
                    text-decoration: underline;
                    border: none;
                    background: none;
                }
            }
        }
    }
    @media (max-width: 600px) {
        .date-picker-mobile-container .date-picker-mobile-wrapper .info {
            padding: 0 16px;
        }
    }
</style>
